/* global tinymce */
/* global app */

import appGraphql from 'api/app_graphql'
import { oauthTokenSelector } from 'selectors/app'
import spinnerImage from 'assets/magic.svg'

const requestMutation = `
    mutation ModifyTone($text: String!, $tone: String!) {
      modifyTone(input: { text: $text, tone: $tone }) {
        jobId
      }
    }
  `
const responseQuery = `
    query EphemeralJobResult($id: ID!) {
      ephemeralJobResult(id: $id) {
        state
        value
      }
    }
  `

tinymce.PluginManager.add('groove-tone-modifier', editor => {
  let overlay

  const requestModification = async (text, tone) => {
    const token = oauthTokenSelector(app.store.getState())
    let result
    try {
      result = await appGraphql(token, requestMutation, {
        text,
        tone,
      })
    } catch (e) {
      return null
    }

    return result.json?.data?.modifyTone?.jobId
  }

  const getJobResult = async function getJobResult(jobId) {
    const token = oauthTokenSelector(app.store.getState())
    return appGraphql(token, responseQuery, { id: jobId })
  }

  const waitForResult = async (jobId, timeoutMs, interval) => {
    const startTime = Date.now()

    const pollForResult = async () => {
      const result = await getJobResult(jobId)
      const { state, value } = result.json?.data?.ephemeralJobResult || {}
      if (state === 'PENDING') {
        if (Date.now() - startTime < timeoutMs) {
          await new Promise(resolve => setTimeout(resolve, interval))
          return pollForResult()
        }
        throw new Error('Timeout exceeded while waiting for the result')
      }

      return value
    }

    return pollForResult()
  }

  const centerRectInContainer = (
    containerRect,
    elementWidth,
    elementHeight
  ) => {
    const containerWidth = containerRect.width
    const containerHeight = containerRect.height

    const offsetX = (containerWidth - elementWidth) / 2
    const offsetY = (containerHeight - elementHeight) / 2

    const elementTop = containerRect.top + offsetY
    const elementLeft = containerRect.left + offsetX

    return { top: elementTop, left: elementLeft }
  }

  const lockEditor = () => {
    editor.setMode('readonly')
    if (overlay) return
    const editorEl = editor.getElement()
    const { top, left, height, width } = editorEl.getBoundingClientRect()
    overlay = document.createElement('div')
    overlay.setAttribute(
      'style',
      `
      position: fixed;
      top: ${top - 3}px;
      left: ${left - 3}px;
      width: ${width + 6}px;
      height: ${height + 6}px;
      z-index: 99999;
      backdrop-filter: blur(3px);
      background-color: rgba(255, 255, 255, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      animation: fadeIn 0.3s ease-in-out;
    `
    )
    const spinner = document.createElement('img')
    // TODO (jscheel): Clean this up
    spinner.src = spinnerImage
    spinner.setAttribute('width', '75')
    spinner.setAttribute('height', '75')
    overlay.appendChild(spinner)
    document.getElementById('overlays').appendChild(overlay)
  }

  const unlockEditor = () => {
    editor.setMode('design')
    if (overlay && overlay.parentNode) {
      overlay.parentNode.removeChild(overlay)
    }
    overlay = null
  }

  const handleClick = async tone => {
    let selectedText
    let mode
    if (editor.selection.isCollapsed) {
      selectedText = editor.getContent({ format: 'text' })
      mode = 'all'
    } else {
      selectedText = editor.selection.getContent({ format: 'text' })
      mode = 'selection'
    }

    if (!selectedText) return

    lockEditor()

    try {
      const jobId = await requestModification(selectedText, tone)
      const summary = await waitForResult(jobId, 30000, 1000)
      if (summary) {
        if (mode === 'all') {
          editor.setContent(summary)
        } else {
          editor.selection.setContent(summary)
        }
      }
    } catch (error) {
      console.error(error)
      alert(
        'An error occurred while processing the text. Please try again later.'
      )
    } finally {
      unlockEditor()
    }
  }

  editor.on('remove', () => {
    if (overlay && overlay.parentNode) {
      overlay.parentNode.removeChild(overlay)
      overlay = null
    }
  })

  editor.addButton('groove-tone-modifier-formal', {
    tooltip: 'More formal',
    icon: 'arrowup',
    async onclick() {
      handleClick('formal')
    },
  })

  editor.addButton('groove-tone-modifier-informal', {
    tooltip: 'More relaxed',
    icon: 'arrowdown',
    async onclick() {
      handleClick('informal')
    },
  })
})
